import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { ChartBar, Package, Storefront, CalendarBlank, Users, ForkKnife, SignOut, House, ShieldCheck } from "@phosphor-icons/react";

const NavItem = ({ to, icon: Icon, label, testid, end }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
        isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      }`
    }>
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="admin min-h-screen flex bg-zinc-50 text-zinc-900" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      <aside className="w-60 shrink-0 border-r border-zinc-200 bg-white flex flex-col">
        <div className="px-5 py-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-zinc-900 flex items-center justify-center text-white text-xs" style={{ fontFamily: "Outfit" }}>S</span>
            <div>
              <p className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>SUNKOP</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Admin Console</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/admin" end icon={ChartBar} label={t("admin.dashboard")} testid="admin-nav-dashboard" />
          <NavItem to="/admin/orders" icon={Package} label={t("admin.orders")} testid="admin-nav-orders" />
          <NavItem to="/admin/events" icon={CalendarBlank} label={t("admin.events_crm")} testid="admin-nav-events" />
          <NavItem to="/admin/customers" icon={Users} label={t("admin.customers")} testid="admin-nav-customers" />
          <NavItem to="/admin/menu" icon={ForkKnife} label={t("admin.menu_mgmt")} testid="admin-nav-menu" />
          <NavItem to="/admin/products" icon={Storefront} label={t("admin.product_mgmt")} testid="admin-nav-products" />
          {user?.role === "owner" && (
            <NavItem to="/admin/users" icon={ShieldCheck} label="Team & Roles" testid="admin-nav-users" />
          )}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-200 space-y-1">
          <NavItem to="/" icon={House} label="View Site" testid="admin-nav-site" />
          <button onClick={doLogout} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-600 hover:bg-zinc-100 hover:text-red-600 transition-colors">
            <SignOut size={18} /> {t("admin.logout")}
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-zinc-200 bg-white flex items-center justify-end px-6 gap-4">
          {user && (
            <div className="flex items-center gap-3">
              {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <div className="text-right">
                <p className="text-xs font-medium" style={{ fontFamily: "Outfit" }}>{user.name}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{user.role}</p>
              </div>
            </div>
          )}
        </header>
        <main className="flex-1 overflow-auto p-8"><Outlet /></main>
      </div>
    </div>
  );
}
